import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaClipboardCheck, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaCheckCircle } from 'react-icons/fa';
import TeacherMultiSelect from './TeacherMultiSelect';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const emptyForm = () => ({
  procedure: '',
  proposedDate: '',
  venueWard: '',
  selectedTeacherIds: []
});

const STATUS_LABEL = { requested: 'Requested', scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled' };
const STATUS_CLASS = { requested: 'ug-status-pending', scheduled: 'ug-status-not-started', completed: 'ug-status-approved', cancelled: 'ug-status-revision' };

const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const UGDoap = ({ onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/doap/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching DOAP sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      procedure: entry.procedure,
      proposedDate: entry.proposedDate ? new Date(entry.proposedDate).toISOString().slice(0, 10) : '',
      venueWard: entry.venueWard || '',
      selectedTeacherIds: (entry.selectedTeacherIds || []).map(t => t._id || t)
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.procedure.trim()) {
      alert('Please fill in Procedure before submitting.');
      return;
    }
    if (!form.selectedTeacherIds.length) {
      alert('Please select at least one faculty member to observe this procedure.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/doap${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchSessions();
      } else {
        alert(data.message || 'Failed to submit request');
      }
    } catch (err) {
      console.error('Error saving DOAP request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Withdraw this DOAP request? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/doap/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchSessions();
      else alert(data.message || 'Failed to withdraw request');
    } catch (err) {
      console.error('Error deleting DOAP request:', err);
    }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaClipboardCheck /> Directly Observed Assessment of Procedural Skills (DOAP)</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Faculty-observed procedure assessments rated across four domains with a global rating.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Request observation
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading DOAP sessions...</div>
      ) : sessions.length === 0 ? (
        <div className="ug-due-empty">
          <p style={{ margin: '0 0 4px 0', fontWeight: 700, color: '#1f2430' }}>No DOAP sessions yet</p>
          <p style={{ margin: '0 0 14px 0' }}>
            Request a Directly Observed Assessment of Procedural Skills. Your faculty observer will rate your execution of clinical procedures using a structured four-domain checklist.
          </p>
          <button className="ug-btn-primary" onClick={openNew}>Request first observation</button>
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {sessions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <span className={`ug-status-badge ${STATUS_CLASS[entry.status] || 'ug-status-not-started'}`}>
                  {entry.status === 'completed' && <FaCheckCircle />} {STATUS_LABEL[entry.status] || entry.status}
                </span>
                {entry.proposedDate && <span className="ug-reflection-date">{formatDate(entry.proposedDate)}</span>}
              </div>
              <h4 className="ug-reflection-period">{entry.procedure}</h4>
              <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                {entry.venueWard && <span>Venue/Ward: {entry.venueWard}</span>}
                {entry.selectedTeacherIds?.length > 0 && (
                  <span>Faculty observer(s): {entry.selectedTeacherIds.map(t => `${t.profile?.firstName} ${t.profile?.lastName}`).join(', ')}</span>
                )}
              </div>

              {entry.status === 'completed' && entry.assessment && (
                <>
                  {entry.assessment.domainScores?.length > 0 && (
                    <>
                      <div className="ug-reflection-section-label">Domain Scores</div>
                      <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                        {entry.assessment.domainScores.map((d, i) => (
                          <span key={i}>{d.domain}: {d.score}</span>
                        ))}
                      </div>
                    </>
                  )}
                  {entry.assessment.globalRating != null && (
                    <>
                      <div className="ug-reflection-section-label">Global Rating</div>
                      <p className="ug-reflection-text">{entry.assessment.globalRating}</p>
                    </>
                  )}
                  {entry.assessment.facultyComments && (
                    <>
                      <div className="ug-reflection-section-label">Faculty Comments</div>
                      <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.assessment.facultyComments}</p>
                    </>
                  )}
                  {entry.assessment.assessedByName && (
                    <p className="ug-reviewer-tag">Assessed by {entry.assessment.assessedByName}</p>
                  )}
                </>
              )}

              {entry.status === 'requested' && (
                <div className="ug-reflection-card-actions">
                  <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                  <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Withdraw"><FaTrash /></button>
                </div>
              )}
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={sessions.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Request DOAP observation</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Procedure</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. IV cannulation, Lumbar puncture"
              value={form.procedure}
              onChange={e => setForm({ ...form, procedure: e.target.value })}
            />

            <label className="ug-form-label">Proposed date (optional)</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.proposedDate}
              onChange={e => setForm({ ...form, proposedDate: e.target.value })}
            />

            <label className="ug-form-label">Venue / Ward (optional)</label>
            <input
              type="text"
              className="ug-form-input"
              value={form.venueWard}
              onChange={e => setForm({ ...form, venueWard: e.target.value })}
            />

            <label className="ug-form-label">Faculty observer(s)</label>
            <TeacherMultiSelect
              value={form.selectedTeacherIds}
              onChange={selectedTeacherIds => setForm({ ...form, selectedTeacherIds })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGDoap;
